import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ProjectSelect from "../../ProjectSelect";
import { WhiteTooltip } from "../../../default_components/Tooltips";
import RichTextEditor from "../../../default_components/rich_text_editor/RichTextEditor";
import Slider from "@mui/material/Slider";
import BookingRepetition from "./components/BookingRepetition";
import HolidayInfo from "./components/HolidayInfo.js";
import {
  useBookingModalStore,
  useBookingsCreatingStore,
  useBookingsEditingStore,
  useSelectedProjectStore,
  useZoomStore,
} from "../../../../stores/planner";
import ProjectBudget from "../../../default_components/ProjectBudget";
import { ClickAwayListener } from "@mui/base";
import { shallow } from "zustand/shallow";
import { InputNumber } from "../../../default_components/InputNumber";
import { TeambookCheckbox } from "../../../default_components/TeambookCheckbox";
import { NavLink } from "react-router-dom";
import { DateTime } from "luxon";
import { TeambookReactSelect } from "../../../default_components/TeambookReactSelect";
import Api from "../../../../Api";
import { TeambookIcon } from "../../../default_images/TeambookIcon";
import { icons } from "../../../default_images/IconsList";
import { useAccountStore } from "../../../../stores/accountStore";
import { StartTimeBlock, calculateStartTime } from "./components/StartTimeBlock";
import CreateActions from "../../lib/create_edit_actions/CreateActions";
import BookingPrecheck from "./components/BookingPrecheck";
import { displayDateTime } from "../../../default_values/defaultDateFormats";
import ActionButtonsRow from "./components/ActionButtonsRow";
import { assignCreationFields, assignEditingFields } from "./helpers/assignEditingFields";
import UsersSelect from "./components/UsersSelect";

const BookingsWindow = ({
  projects,
  closeModal,
  createBookingsRequest,
  createBookingsRef,
  taskArray,
  setTaskArray,
  orderedPlannerUsers,
  collapsedUsers,
  deleteBookingsRequest,
  allUsers,
  updateBookingsRequest,
  deleteRepetitionClick,
}) => {
  const { t } = useTranslation();
  const role = localStorage.getItem("tb-role") || "regular";
  const editorRef = useRef();
  const windowRef = useRef();
  const createActions = new CreateActions();

  const [zoom] = useZoomStore((state) => [state.zoom], shallow);
  const [editingBookings, editingDuration, setEditingDuration, editWindowOpened] = useBookingsEditingStore(
    (state) => [state.bookingsEditing, state.editingDuration, state.setEditingDuration, state.editWindowOpened],
    shallow
  );
  const [createWindowOpened, bookingsDuration, setBookingsDuration, dayViewStartTime, bookingsCreating] =
    useBookingsCreatingStore(
      (state) => [
        state.createWindowOpened,
        state.bookingsDuration,
        state.setDuration,
        state.dayViewStartTime,
        state.bookingsCreating,
      ],
      shallow
    );
  const { selectedProject, selectedTimeOff, setSelectedProject, setSelectedTimeOff, projectsType, setProjectsType } =
    useSelectedProjectStore();
  const [account] = useAccountStore((state) => [state.account], shallow);
  const [setRepetition, setRepetitionComponentOpen] = useBookingModalStore(
    (state) => [state.setRepetition, state.setRepetitionComponentOpen],
    shallow
  );

  const [tentative, setTentative] = useState(false);
  const [startTime, setStartTime] = useState();
  const [startTimeWindowOpened, setStartTimeWindowOpened] = useState(false);
  const [location, setLocation] = useState(0);
  const [usesBudget, setUsesBudget] = useState(true);
  const [task, setTask] = useState();
  const [deleteBookingsAfterSave, setDeleteBookingsAfterSave] = useState(false);
  const [usersToCreateBookings, setUsersToCreateBookings] = useState([]);
  const [hideTimeOffOptions, setHideTimeOffOptions] = useState(false);

  const modalType = editWindowOpened ? "editing" : "creating";

  useEffect(() => {
    if (!createWindowOpened && !editWindowOpened) return;

    if (windowRef.current) {
      windowRef.current.focus();
    }
  }, [createWindowOpened, editWindowOpened]);

  useEffect(() => {
    setHideTimeOffOptions(projectsType === "time_off" && !projects.some((pr) => pr.kind === "time_off"));
  }, [projectsType, projects]);

  useEffect(() => {
    if (createWindowOpened) {
      setDuration(createBookingsRef?.current[0]?.duration || account.default_booking_duration / 60);
    }
  }, [createWindowOpened]);

  useEffect(() => {
    if (!createWindowOpened) return;

    if (!projects.find((pr) => selectedProject?.id === pr.id)) {
      setSelectedProject(projects.find((pr) => pr.active && pr.kind === selectedProject?.kind) || projects[0]);
    }
    setStartTime(calculateStartTime(account, createBookingsRef));
  }, [createWindowOpened]);

  useEffect(() => {
    if (zoom === 1) {
      setStartTime(dayViewStartTime);
      setStartTimeWindowOpened(true);
    }
  }, [dayViewStartTime]);

  useEffect(() => {
    if (modalType !== "editing" || editWindowOpened === false) return;

    assignEditingFields(
      editingBookings,
      projects,
      setEditingDuration,
      setTentative,
      setLocation,
      setStartTime,
      setStartTimeWindowOpened,
      setUsesBudget,
      setTask,
      setRepetition,
      setUsersToCreateBookings,
      setDeleteBookingsAfterSave,
      setRepetitionComponentOpen
    );
  }, [editWindowOpened, editingBookings, projects]);

  useEffect(() => {
    if (modalType !== "creating" || createWindowOpened === false) return;

    assignCreationFields(
      setTentative,
      setLocation,
      setStartTime,
      setStartTimeWindowOpened,
      setUsesBudget,
      setTask,
      setRepetition,
      setUsersToCreateBookings,
      setDeleteBookingsAfterSave,
      setRepetitionComponentOpen
    );
  }, [createWindowOpened]);

  const setDuration = (new_duration) => {
    createBookingsRef.current.forEach((ref) => {
      if (ref) {
        ref.updateDuration(new_duration);
      }
    });

    setBookingsDuration(new_duration);
  };

  const onKeyPress = (e) => {
    if (e.key === "Escape") {
      closeModal();
    }
  };

  const sliderMarks = [
    {
      value: 0,
    },
    {
      value: 1,
    },
    {
      value: 2,
    },
  ];

  const clickedOutsideOfPlanner = (e) => {
    const n = e?.target?.className ?? "";
    if (typeof n !== "string") return false;

    return (
      !e?.target?.className?.split(" ").includes("booking") &&
      e.target.className !== "creation-booking" &&
      e.target.className !== "date-row__date-text" &&
      e.target.className !== "planner__user-row-list" &&
      Boolean(e.target.closest(".user-row")) === false
    );
  };

  const changeProjectType = (newProjectType) => {
    let notSet = true;

    if (projectsType !== newProjectType && projects.size > 0) {
      projects.forEach((project) => {
        if (project.kind === "time_off" && newProjectType === "time_off" && notSet) {
          notSet = false;
        } else if (project.kind !== "time_off" && newProjectType !== "time_off" && notSet) {
          notSet = false;
        }
      });
    }

    let newProject =
      newProjectType === "time_off"
        ? projects.find((pr) => pr.kind === "time_off")
        : projects.find((pr) => pr.kind !== "time_off");

    if (newProjectType === "time_off" && selectedTimeOff === null) {
      setSelectedTimeOff(newProject);
    } else if (newProjectType === "default" && selectedProject === null) {
      setSelectedProject(newProject);
    }

    setProjectsType(newProjectType);
  };

  const selectDefaultProjects = () => {
    if (modalType === "editing") {
      changeProjectType("default");
    } else {
      let activeProjects = projects.filter((project) => project.active && project.kind !== "time_off");

      if (activeProjects.length > 0) {
        let projectToSelect = activeProjects[0];

        setProjectsType("default");
        setDuration(account.default_booking_duration / 60);

        if (selectedProject === null || selectedProject.kind !== "default") {
          setSelectedProject(projectToSelect);
        }
      } else {
        alert("No Active projects found!");
      }
    }
  };

  const selectTimeOffProjects = () => {
    if (modalType === "editing") {
      changeProjectType("time_off");
    } else {
      createActions.selectTimeOffProjects(
        setProjectsType,
        createBookingsRef,
        setBookingsDuration,
        projects,
        selectedProject,
        selectedTimeOff,
        setSelectedTimeOff
      );
    }
  };

  const taskOptions = () => {
    const newTaskOptions = taskArray.filter(
      (t) =>
        t.project_id === null || t.project_id === (projectsType === "default" ? selectedProject : selectedTimeOff)?.id
    );

    if (newTaskOptions.length > 0) newTaskOptions.push({ name: t("actuals.no_task"), id: null, project_id: null });

    return newTaskOptions;
  };

  const bookingsAmount = () => {
    if (modalType === "editing") {
      return editingBookings.length;
    } else {
      return bookingsCreating.length;
    }
  };

  const createBookingHeader = () => {
    const messages = {
      editing: {
        single: `${t("planning.edit_booking")}: ${DateTime.fromISO(editingBookings?.[0]?.date).toFormat("d LLL, y")}`,
        multiple: t("planning.editing_n_bookings", { number: editingBookings.length }),
      },
      creating: {
        single: `${t("planning.create_booking")}: ${DateTime.fromJSDate(bookingsCreating[0]?.[1]).toFormat(
          "d LLL, y"
        )}`,
        multiple: t("planning.create_booking"),
      },
    };

    if (bookingsAmount() === 1) {
      return messages[modalType].single;
    } else {
      return messages[modalType].multiple;
    }
  };

  const getCreationUsers = () => {
    if (modalType === "editing") {
      return editingBookings.map((b) => b.user_id);
    } else {
      return bookingsCreating.map((user) => orderedPlannerUsers[user[0]]?.id).filter(Number);
    }
  };

  const changeDuration = (newDuration) => {
    if (modalType === "editing") {
      setEditingDuration(newDuration);
    } else {
      setDuration(newDuration);
    }
  };

  const currentDuration = () => {
    if (modalType === "editing") {
      return editingDuration;
    } else {
      return bookingsDuration;
    }
  };

  const nameById = (id) => {
    if (!id) {
      return "undefined";
    }

    let user = allUsers.filter((obj) => obj.id === id)[0];

    if (!user) {
      return "undefined";
    } else {
      return `${user.first_name} ${user.last_name[0]}`;
    }
  };

  if (!createWindowOpened && !editWindowOpened) {
    return null;
  }

  return (
    <ClickAwayListener
      onClickAway={(e) => {
        if (clickedOutsideOfPlanner(e)) {
          closeModal();
        }
      }}
    >
      <div className="booking-window" tabIndex="0" onKeyDown={onKeyPress} id="create-window" ref={windowRef}>
        <div className="booking-window__header">
          <p>{createBookingHeader()}</p>
        </div>

        {hideTimeOffOptions ? (
          <div className="booking-window__empty_timeoffs">
            <p>
              {t("planning.no_timeoffs_1")}{" "}
              <NavLink
                to={{
                  pathname: "/projects",
                }}
              >
                {t("planning.time_off_link")}
              </NavLink>{" "}
              {t("planning.no_timeoffs_2")}
            </p>
          </div>
        ) : (
          <div className="booking-window__booking-data-block">
            <div className="row">
              <ProjectSelect
                projects={projects.filter((p) => p.active)}
                projectsType={projectsType}
                creationUsers={getCreationUsers()}
                selectDefaultProjects={selectDefaultProjects}
                selectTimeOffProjects={selectTimeOffProjects}
              />
            </div>

            {projectsType === "time_off" && <HolidayInfo />}

            {projectsType === "default" &&
              (selectedProject?.estimated > 0 || selectedProject?.actual_logs_duration > 0) && (
                <div className="row booking-window__project-estimation">
                  <ProjectBudget project={selectedProject} actuals={true} />
                </div>
              )}

            <div className="row booking-window__data-element">
              <p className="col-md-8 booking-window__data-element-text">{t("planning.duration_hours")}</p>

              <InputNumber
                id={"duration"}
                max={24}
                min={0.25}
                step={0.5}
                setValue={changeDuration}
                value={currentDuration()}
                roundValue={{
                  isRoundValue: true,
                  roundStep: 4,
                }}
                arrowSize={16}
              />
            </div>

            <div className="booking-window__grouped-data-element">
              <div className="row" style={{ justifyContent: "space-between" }}>
                <TeambookIcon
                  tooltipTitle={t("planning.at_office")}
                  onClick={() => setLocation(0)}
                  name={icons.AT_WORK}
                  placement="top"
                  style={{ opacity: location === 0 ? 1 : 0.3 }}
                />

                <TeambookIcon
                  id={"atClientSieBooking"}
                  alt={"on client"}
                  onClick={() => setLocation(1)}
                  name={icons.PLACE}
                  tooltipTitle={t("planning.on_client_site")}
                  placement="top"
                  style={{ opacity: location === 1 ? 1 : 0.3 }}
                />

                <TeambookIcon
                  alt={"home office"}
                  onClick={() => {
                    setLocation(2);
                  }}
                  name={icons.HOME}
                  tooltipTitle={t("planning.home_office")}
                  placement="top"
                  style={{ opacity: location === 2 ? 1 : 0.3 }}
                />
              </div>

              <div className="row locationPicker" style={{ padding: "0 12px" }}>
                <Slider
                  track={false}
                  max={2}
                  value={location}
                  aria-labelledby="discrete-slider-restrict"
                  step={null}
                  onChangeCommitted={(_, val) => setLocation(val)}
                  marks={sliderMarks}
                />
              </div>
            </div>

            <UsersSelect
              createActions={createActions}
              collapsedUsers={collapsedUsers}
              orderedPlannerUsers={orderedPlannerUsers}
              createWindowOpened={createWindowOpened}
              createBookingsRef={createBookingsRef}
              usersToCreateBookings={usersToCreateBookings}
              setUsersToCreateBookings={setUsersToCreateBookings}
            />

            <div style={{ marginTop: 16 }} className="row booking-window__grouped-data-element">
              <TeambookReactSelect
                fieldName={t("actuals.service")}
                value={taskArray.filter((subTask) => subTask.id === task && subTask.id)?.[0]}
                createOptionPosition={"first"}
                formatCreateLabel={(option) => `${t("projects.create_task")} ${option}`}
                onCreateOption={(option) => {
                  Api.Tasks.create({
                    project_id: (projectsType === "time_off" ? selectedTimeOff : selectedProject).id,
                    name: option,
                  }).then((res) => {
                    setTaskArray([...taskArray, res.data]);
                    setTask(res.data.id);
                  });
                }}
                onChange={(task) => {
                  setTask(task.value);
                }}
                isValidNewOption={(option) => ["admin", "planner", "self_planner"].includes(role) && option.length > 2}
                options={taskOptions().map((t) => ({ value: t.id, name: t.name }))}
                isSearchable
                nameStyles={{ fontSize: 14, textAlign: "left" }}
                grayPlaceholder
                placeholder={taskOptions().length === 0 ? t("planning.no_tasks") : t("planning.select_task")}
              />
            </div>

            <div style={{ marginTop: 16 }} className="row booking-window__grouped-data-element">
              <div className="row checkbox">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <TeambookCheckbox
                    checked={tentative}
                    onChange={() => {
                      setTentative(!tentative);
                    }}
                    value={tentative}
                    color="primary"
                  />

                  <p className="booking-window__data-element-text" onClick={() => setTentative(!tentative)}>
                    {t("planning.tentative")}
                  </p>
                </div>

                {selectedProject?.kind === "billable" && (
                  <div style={{ display: "flex" }}>
                    <TeambookCheckbox
                      checked={!usesBudget}
                      onChange={() => {
                        setUsesBudget(!usesBudget);
                      }}
                      color="primary"
                    />

                    <WhiteTooltip title={t("planning.uses_budget_description")} placement="top">
                      <p className="booking-window__data-element-text" onClick={() => setUsesBudget(!usesBudget)}>
                        {t("planning.uses_budget")}
                      </p>
                    </WhiteTooltip>
                  </div>
                )}
              </div>

              <StartTimeBlock
                startTime={startTime}
                setStartTime={setStartTime}
                startTimeWindowOpened={startTimeWindowOpened}
                setStartTimeWindowOpened={setStartTimeWindowOpened}
                account={account}
                createBookingsRef={createBookingsRef}
                bookingsDuration={bookingsDuration}
              />

              <BookingRepetition
                creationBookingsArray={
                  modalType === "editing"
                    ? [[editingBookings[0].user_id, new Date(editingBookings[0].date)]]
                    : bookingsCreating
                }
                booking={editingBookings?.[0] || null}
                canDeleteRepetition={editingBookings?.length > 0}
                deleteRepetitionClick={deleteRepetitionClick}
                createWindowOpened={createWindowOpened}
                createBookingsRef={createBookingsRef}
              />
            </div>

            <div style={{ marginTop: 16 }}>
              <div className="planner-editor">
                <RichTextEditor
                  editorHeight={100}
                  ref={editorRef}
                  value={modalType === "editing" ? editingBookings?.[0]?.comment : undefined}
                />
              </div>
            </div>

            {modalType === "editing" && editingBookings.length === 1 && !["contractor"].includes(role) && (
              <>
                <p className="booking-window__last-change-info-first">
                  Created by {nameById(editingBookings[0].created_by)} at{" "}
                  {displayDateTime(editingBookings[0].created_at, account.date_format)}
                </p>
                <p className="booking-window__last-change-info-second">
                  Updated by {nameById(editingBookings[0].updated_by)} at{" "}
                  {displayDateTime(editingBookings[0].updated_at, account.date_format)}
                </p>
              </>
            )}
          </div>
        )}

        <div className="booking-window__control-buttons">
          <BookingPrecheck popupMode={modalType} users={orderedPlannerUsers} />

          <ActionButtonsRow
            closeModal={closeModal}
            modalType={modalType}
            hideTimeOffOptions={hideTimeOffOptions}
            createBookingsRequest={createBookingsRequest}
            tentative={tentative}
            startTimeWindowOpened={startTimeWindowOpened}
            startTime={startTime}
            editorRef={editorRef}
            usesBudget={usesBudget}
            task={task}
            deleteBookingsAfterSave={deleteBookingsAfterSave}
            updateBookingsRequest={updateBookingsRequest}
            usersToCreateBookings={usersToCreateBookings}
            orderedPlannerUsers={orderedPlannerUsers}
            createBookingsRef={createBookingsRef}
            location={location}
            deleteBookingsRequest={deleteBookingsRequest}
          />
        </div>
      </div>
    </ClickAwayListener>
  );
};

BookingsWindow.propTypes = {
  projects: PropTypes.array,
  closeModal: PropTypes.func,
  createBookingsRequest: PropTypes.func,
  createBookingsRef: PropTypes.object,
  taskArray: PropTypes.array,
  setTaskArray: PropTypes.func,
  orderedPlannerUsers: PropTypes.array,
  collapsedUsers: PropTypes.array,
  deleteBookingsRequest: PropTypes.func,
  allUsers: PropTypes.array,
  updateBookingsRequest: PropTypes.func,
  deleteRepetitionClick: PropTypes.func,
};

export default BookingsWindow;
